import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronLeft, ChevronRight, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/shared/Button';
import { AttemptQuestionImage } from '../components/exam/AttemptQuestionImage';
import { ExamContentGuard } from '../components/exam/ExamContentGuard';
import { examService, type ResumeAttemptResponse } from '../services/exam.service';

type Question = ResumeAttemptResponse['exam']['questions'][number];

export const KnmExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [expiresAt, setExpiresAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const answersRef = useRef<Record<string, string>>({});
  const dirtyRef = useRef<Set<string>>(new Set());
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const submittingRef = useRef(false);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!attemptId) { navigate('/dashboard'); return; }
    let cancelled = false;
    examService.resumeAttempt(attemptId).then((data) => {
      if (cancelled) return;
      if (data.exam.type !== 'knm' || data.exam.questions.length === 0) {
        setError(t('knm.invalidAttempt'));
        setLoading(false);
        return;
      }
      const restored: Record<string, string> = {};
      for (const answer of data.savedAnswers || []) {
        if (typeof answer.answer === 'string') restored[answer.questionId] = answer.answer;
      }
      answersRef.current = restored;
      setAnswers(restored);
      setQuestions(data.exam.questions);
      const end = new Date(data.expiresAt).getTime();
      setExpiresAt(end);
      setRemaining(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
      setLoading(false);
    }).catch((reason: any) => {
      if (!cancelled) {
        setError(reason?.response?.data?.message || t('knm.loadError'));
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [attemptId, navigate, t]);

  const submit = useCallback(async () => {
    if (!attemptId || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSaveError(null);
    try {
      await saveQueueRef.current.catch(() => undefined);
      // Retry any last or failed saves before scoring. Saved answers are text,
      // so option shuffling on resume cannot change what the candidate chose.
      for (const questionId of Array.from(dirtyRef.current)) {
        await examService.saveAnswer(attemptId, questionId, answersRef.current[questionId]);
        dirtyRef.current.delete(questionId);
      }
      await examService.submitExam(attemptId);
      navigate(`/results?attemptId=${attemptId}`);
    } catch (reason: any) {
      setSaveError(reason?.response?.data?.message || t('knm.saveError'));
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [attemptId, navigate, t]);

  useEffect(() => {
    if (!expiresAt || !questions.length) return;
    const timer = window.setInterval(() => {
      const seconds = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemaining(seconds);
      if (seconds === 0) { window.clearInterval(timer); void submit(); }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt, questions.length, submit]);

  const choose = (question: Question, answer: string) => {
    if (!attemptId || submittingRef.current) return;
    const next = { ...answersRef.current, [question._id]: answer };
    answersRef.current = next;
    setAnswers(next);
    dirtyRef.current.add(question._id);
    setSaveError(null);
    saveQueueRef.current = saveQueueRef.current.catch(() => undefined).then(async () => {
      await examService.saveAnswer(attemptId, question._id, answer);
      if (answersRef.current[question._id] === answer) dirtyRef.current.delete(question._id);
    }).catch(() => { setSaveError(t('knm.saveError')); });
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /><span className="ml-3">{t('common.loading')}</span></div>;
  if (error || questions.length === 0) return <div className="mx-auto max-w-xl p-8 text-center"><p className="mb-4 text-error">{error || t('knm.loadError')}</p><Button onClick={() => navigate('/dashboard')}>{t('examSelection.backToDashboard')}</Button></div>;

  const current = questions[index];
  const selected = answers[current._id];
  const answered = questions.filter((question) => answers[question._id] != null).length;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <ExamContentGuard>
      <div className="min-h-screen bg-slate-50 pb-12">
        <header className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
            <div><h1 className="text-xl font-bold text-ink">{t('knm.title')}</h1><p className="text-sm text-slate-600">{t('knm.questionCount', { current: index + 1, total: questions.length })}</p></div>
            <div className="flex items-center gap-2 font-semibold text-primary"><Clock className="h-4 w-4" />{minutes}:{String(seconds).padStart(2, '0')}</div>
          </div>
        </header>
        <main className="mx-auto max-w-4xl space-y-5 px-4 py-7">
          <div className="surface-card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
              <span>{t('knm.theme')}: {current.theme}</span>
              <span>{t('knm.answered', { count: answered, total: questions.length })}</span>
            </div>
            {attemptId && current.media?.image && <AttemptQuestionImage attemptId={attemptId} questionId={current._id} theme={current.theme} />}
            <h2 className="mb-6 text-xl font-semibold text-ink">{current.prompt || current.text}</h2>
            <div className="space-y-3">
              {current.options.map((option, optionIndex) => (
                <button key={`${current._id}-${optionIndex}`} type="button" onClick={() => choose(current, option.text)}
                  className={`flex w-full items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors ${selected === option.text ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-primary/40'}`}>
                  <span className="font-bold text-primary">{String.fromCharCode(65 + optionIndex)}</span><span>{option.text}</span>
                </button>
              ))}
            </div>
            {saveError && <p role="alert" className="mt-4 text-sm text-error">{saveError}</p>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="tertiary" disabled={index === 0} onClick={() => setIndex(index - 1)} icon={<ChevronLeft className="h-4 w-4" />}>{t('answerReview.previous')}</Button>
            <Button variant="tertiary" disabled={index === questions.length - 1} onClick={() => setIndex(index + 1)} icon={<ChevronRight className="h-4 w-4" />}>{t('answerReview.next')}</Button>
          </div>
          <div className="flex flex-wrap gap-2" aria-label={t('knm.questionNavigation')}>
            {questions.map((question, questionIndex) => <button key={question._id} type="button" onClick={() => setIndex(questionIndex)} aria-label={t('knm.goToQuestion', { number: questionIndex + 1 })}
              className={`h-9 w-9 rounded-lg border text-sm font-semibold ${questionIndex === index ? 'border-primary bg-primary text-white' : answers[question._id] ? 'border-primary/40 bg-primary/10 text-primary' : 'border-slate-300 bg-white text-slate-700'}`}>{questionIndex + 1}</button>)}
          </div>
          <Button variant="primary" onClick={() => setConfirmSubmit(true)}>{t('exam.common.submitExam')}</Button>
        </main>
        {confirmSubmit && <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/50 p-4"><div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
          <AlertTriangle className="mb-3 h-8 w-8 text-warning" /><h2 className="mb-2 text-xl font-bold">{t('exam.common.confirmSubmit')}</h2>
          <p className="mb-4 text-slate-600">{t('knm.unanswered', { count: questions.length - answered })}</p>
          {saveError && <p role="alert" className="mb-4 text-error">{saveError}</p>}
          <div className="flex gap-3"><Button variant="tertiary" disabled={submitting} onClick={() => setConfirmSubmit(false)}>{t('exam.common.backToExam')}</Button><Button variant="primary" loading={submitting} onClick={() => void submit()}>{t('exam.common.submit')}</Button></div>
        </div></div>}
      </div>
    </ExamContentGuard>
  );
};

export default KnmExamPage;
